import Pagination from '@mui/material/Pagination';
import { makeStyles } from '@mui/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EmpleadosAccordion } from '../../Components/AccordionEmpleados/Empleados';
import { BarraDeBusquedaMemo } from '../../Components/Empleado/BarraDeBusqueda';
import { Encabezado } from '../../Components/Encabezado/Encabezado';
import Loading from '../../Components/Modals/Loading/Loader';
import { usePreviousRoute } from '../../context/PreviousRouteProvider';

import { fetchAreasOrganizacionales } from '../../redux/AreaOrganizacional/AreaOrganizacionalSlice';
import { fetchCargosAll } from '../../redux/Cargos/CargosSlice';
import {
  clearUltimoFiltro,
  desvincularEmpleado,
  selectAllEmpleados,
  selectEmpleadosCount,
  selectEmpleadosIsLoading,
  setUltimoFiltro,
} from '../../redux/Empleados/EmpleadosSlice';

import DesvincularEmpleado from '../../Components/Modals/Empleados/DesvincularEmpleado';

import { loadEmpleados } from './Funcs/Paginacion';

import './Empleados.scss';

const useStyles = makeStyles(() => ({
  ul: {
    '& .MuiPaginationItem-root': {
      color: '#003876',
    },
  },
}));

// * VALORES INICIALES DEL FILTRO.
let filtroInitialValuesState = {
  consdex: 0,
  itemsPerPage: 15,
  nombre: '',
  apellido: '',
  areaOrganizacionalId: 0,
  cargoId: 0,
  estado: 1,
  codigo: '',
};

export function Empleados() {
  // ? HOOK-DISPATCH
  const dispatch = useDispatch();

  // ? HOOK-PREVIOUS-ROUTE
  const previousRoute = usePreviousRoute(); // Obtener la ruta anterior

  // ? HOOK-SELECTOR
  const empleados = useSelector(selectAllEmpleados);
  const empleadosLoading = useSelector(selectEmpleadosIsLoading);
  const empleaodsCount = useSelector(selectEmpleadosCount);
  const ultimoFiltro = useSelector((s) => s.empleados.ultimoFiltro);

  // ? HOOK-STATE
  const [page, setPage] = useState(0);
  const [addRequestStatus, setAddRequestStatus] = useState('idle');
  const [updateListApiState, setUpdateListApiState] = useState(false);
  const [open, setOpen] = useState({ active: false, empleado: {} });
  const [filtroState, setFiltroState] = useState(Object.keys(ultimoFiltro).length > 0 ? ultimoFiltro : filtroInitialValuesState);

  const handleChangePage = (event, newPage) => {
    setUpdateListApiState(true);
    setFiltroState({ ...filtroState, index: (newPage - 1) * filtroInitialValuesState.itemsPerPage });
    setPage(newPage - 1);
  };

  // * FUNCION PARA DESVINCULAR EMPLEADO.
  const handleDesvincularEmpleado = (empleadoDesvinculado) => {
    try {
      setAddRequestStatus('success');
      dispatch(desvincularEmpleado(empleadoDesvinculado));
    } catch (error) {
      setAddRequestStatus('error');
    }
  };

  // * FUNCION PARA REFRESCAR LISTA DE EMPLEADOS SEGUN LAS CONDICIONES.
  const cargarEmpleados = () => {
    dispatch(loadEmpleados(filtroState));
    setPage(filtroState.index / filtroInitialValuesState.itemsPerPage);
  };

  // * FUNCION PARA LIMPIAR FILTRO.
  const cleanfilter = () => {
    // * LIMPIANDO HISTORICO DE REDUX.
    dispatch(clearUltimoFiltro());

    setPage(0);
    setFiltroState(filtroInitialValuesState);
    setUpdateListApiState(true);
  };

  // ? HOOK-EFFECT DE LAS LISTAS.
  useEffect(() => {
    dispatch(fetchAreasOrganizacionales());
    dispatch(fetchCargosAll());
  }, []);

  // ? HOOK-EFFECT PARA ACTUALIZAR LISTADO.
  useEffect(() => {
    if (updateListApiState) {
      cargarEmpleados();
      setUpdateListApiState(false);
    }
  }, [updateListApiState, filtroState]);

  // ? HOOK-EFFECT PARA LIMPIAR FILTROS E HISTORIAL SI EL USUARIO VIENE DE OTRA RUTA QUE NO
  // ? SEA DE EMPLEADOS.
  useEffect(() => {
    // * VALIDACIONES
    const validacionRutaEmpleado = previousRoute?.includes('/empleados');
    const validacionObjetoAlmacenado = Object.keys(ultimoFiltro).length > 0;
    const validacionEsMismoEstado = ultimoFiltro.estado === filtroInitialValuesState.estado;

    if (!validacionRutaEmpleado) {
      cleanfilter();
      return;
    }

    if (!validacionObjetoAlmacenado) {
      cargarEmpleados();
      return;
    }

    if (!validacionEsMismoEstado) {
      cleanfilter();
      return;
    }

    setFiltroState(ultimoFiltro);
    setUpdateListApiState(true);
  }, [previousRoute]);

  // * FUNCION PARA GUARDAR ULTIMO FILTRO.
  const saveUltimoFiltro = useCallback(
    (filtro) => {
      dispatch(setUltimoFiltro(filtro));
    },
    [dispatch]
  );

  // ? HOOK-MEMO PARA ALMACENAR LA DATA DEL LISTADO DE EMPLEADOS Y EVITAR QUE SE RENDERICE.
  const data = useMemo(() => empleados, [empleados]);

  // * CLASES DE ESTILOS
  const classes = useStyles();

  return (
    <Encabezado title="Empleados">
      <BarraDeBusquedaMemo
        filtroState={filtroState}
        setFiltroState={setFiltroState}
        setUpdateListApiState={setUpdateListApiState}
        setPage={setPage}
        cleanfilter={cleanfilter}
        cargarEmpleados={cargarEmpleados}
      />

      {empleados && empleados.length > 0 && (
        <EmpleadosAccordion empleados={data} open={open} setOpen={setOpen} saveUltimoFiltro={saveUltimoFiltro} filtroState={filtroState} />
      )}

      {empleados && empleados.length > 0 ? (
        <div className="p-4 d-flex justify-content-center">
          <Pagination
            classes={{ ul: classes.ul }}
            variant="outlined"
            shape="rounded"
            count={
              Math.ceil(empleaodsCount / filtroInitialValuesState.itemsPerPage) < 1
                ? 1
                : Math.ceil(empleaodsCount / filtroInitialValuesState.itemsPerPage)
            }
            page={page + 1}
            onChange={handleChangePage}
            boundaryCount={10}
            color="primary"
          />
        </div>
      ) : (
        <p>No se encontraron empleados</p>
      )}

      <DesvincularEmpleado
        addRequestStatus={addRequestStatus}
        cleanfilter={cleanfilter}
        setAddRequestStatus={setAddRequestStatus}
        open={open}
        setOpen={setOpen}
        handleDesvincularEmpleado={handleDesvincularEmpleado}
      />

      <Loading open={empleadosLoading} />
    </Encabezado>
  );
}

export default Empleados;
